<script setup lang="ts">
import type { RouteLocationResolved } from 'vue-router'

defineProps<{
  // If to is undefined, the card is not clickable, which is useful for previewing
  to?: string | RouteLocationResolved<string>
  // Title to be displayed in the card
  title?: string
  // Title to be displayed in the header
  headerTitle?: string
  description?: string
  imageUrl?: string | null
  headerTags?: Array<{
    text: string
    icon?: string
  }>
  bodyTags?: Array<{
    text: string
    icon?: string
  }>
  organizationName?: string
  headerClass?: string
  // TODO: merge this with tags.
  dates?: string[]
  // TODO: merge this with tags.
  remainingDatesLabel?: string
}>()

const { defaultShapes } = useTheme()
const { cardRadiusClass } = useThemeClasses()
const isRounded = computed(() => {
  return defaultShapes.value?.card == 'rounded'
})

// Crap, what were v1 and v2 again? Is v1 still being used somewhere?
const cardStyle = ref<'v1' | 'v2'>('v2')

</script>

<template>
  <NuxtLink
    :to="to || '#'"
    :aria-label="title ? `Details van vacature ${title} bekijken` : 'Details van vacature bekijken'"
    class="focus-visible:ring-primary-500 group block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
    :class="[cardRadiusClass, { 'pointer-events-none': !to }]"
  >
    <BaseCard
      :shape="isRounded ? 'curved' : 'straight'"
      class="dotted-outer-border flex h-full flex-col"
      :class="{
        'dotted-outer-border-rounded': isRounded,
        'px-5 py-4': cardStyle == 'v2',
        'hover:cursor-pointer': !!to
      }"
    >
      <!-- Card Header with Image -->
      <div
        :class="[
          'bg-primary-900 relative overflow-hidden text-white',
          headerClass || 'h-40',
          isRounded && cardStyle == 'v1' ? 'rounded-t-xl' : '',
          isRounded && cardStyle == 'v2' ? cardRadiusClass : '',
        ]"
      >
        <img
          v-if="imageUrl"
          :src="imageUrl"
          :alt="title || 'Vacancy image'"
          class="absolute inset-0 size-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
        >
        <SearchCardImagePlaceholder v-else aria-hidden="true" />

        <!-- Header title - only used for Stageplein right now? -->
        <slot name="header-title">
          <BaseHeading
            v-if="headerTitle"
            as="h4"
            size="lg"
            weight="semibold"
            lead="tight"
            class="after:text-muted-800 absolute bottom-1 left-0 z-20 mb-4 w-full px-6 transition-all duration-300 group-hover:translate-y-0.5 dark:text-white"
          >
            <span>{{ headerTitle }}</span>
          </BaseHeading>
        </slot>

        <!-- Image overlay hover effect -->
        <div
          v-if="imageUrl"
          class="bg-muted-700 absolute inset-0 z-10 size-full opacity-0 transition-opacity duration-300 group-hover:opacity-10"
        />

        <!-- Tags in header slot -->
        <slot name="header-tags">
          <SearchCardHeaderTags
            :organization-name="organizationName"
            :header-tags="headerTags"
          />
        </slot>
      </div>

      <!-- Card Content -->
      <div
        :class="{
          'p-6': cardStyle == 'v1',
          'flex flex-wrap items-center gap-2 px-0 pb-2 pt-4': cardStyle == 'v2',
        }"
      >
        <!-- Content Title (alternative to header title) -->
        <slot name="content-title">
          <BaseHeading
            v-if="title"
            as="h4"
            size="lg"
            weight="semibold"
            lead="tight"
            class="after:text-theme-content w-full"
          >
            <span>{{ title }}</span>
          </BaseHeading>
        </slot>

        <!-- Description -->
        <BaseParagraph v-if="description" size="sm">
          <span class="line-clamp-4">
            {{ description }}
          </span>
        </BaseParagraph>

        <!-- Tags in content -->
        <slot name="content-tags">
          <div v-if="bodyTags?.length || dates?.length" class="flex flex-wrap items-center gap-2 py-4">
            <BaseTag
              v-for="tag in bodyTags"
              :key="tag.text"
              color="default"
              size="sm"
              class="text-xs"
            >
              <Icon v-if="tag.icon" :name="tag.icon" />
              {{ tag.text }}
            </BaseTag>

          <!-- <BaseTag
            v-for="date in dates"
            :key="date"
            color="muted"
            size="sm"
          >
            {{ date }}
          </BaseTag>

          <BaseTag
            v-if="remainingDatesLabel"
            color="muted"
            size="sm"
          >
            {{ remainingDatesLabel }}
          </BaseTag> -->
          </div>
        </slot>

        <!-- Additional content slot -->
        <slot name="content" />
      </div>
    </BaseCard>
  </NuxtLink>
</template>

<style scoped>
.dotted-outer-border:hover::before {
  @apply border-primary-500 pointer-events-none absolute -inset-1 block border border-dashed opacity-50;
  content: '';
}
.dotted-outer-border-rounded:hover::before {
  @apply rounded-2xl;
}
</style>
